import * as joint from '@joint/plus';
import { shapes } from '@joint/plus';
import { Div, Tri, Stock } from './custom-shapes';
import { getAttrsByLinkType } from './links';

// Ajouter la méthode calculateLength à standard.Link
joint.shapes.standard.Link.prototype.calculateLength = function () {
  const cell = this;
  const paper = cell.graph?.paper;

  // Helper: current zoom factor (PaperScroller applies CSS transform)
  const getScaleFactor = () => {
    if (paper && typeof paper.scale === 'function') {
      const s = paper.scale();
      if (s && typeof s.sx === 'number' && typeof s.sy === 'number') {
        // uniform zoom in this app; average guards against anisotropic scale
        return (s.sx + s.sy) / 2;
      }
    }
    return 1;
  };

  const maybeAttachPortsUpdater = (view) => {
    if (!view) return;
    cell.off('change:ports');
    cell.on('change:ports', function () {
      view.updatePorts();
    });
  };

  const finalizeLength = (modelLen, view) => {
    const cssLen = modelLen * getScaleFactor();
    maybeAttachPortsUpdater(view);
    return Math.round(cssLen);
  };

  // 1) Preferred: measure the rendered SVG path
  if (paper && typeof paper.findViewByModel === 'function') {
    const view = paper.findViewByModel(cell);
    if (view) {
      const pathEl = view.el.querySelector('path.connection') || view.el.querySelector('path');

      if (pathEl && typeof pathEl.getTotalLength === 'function') {
        // getTotalLength is in SVG user units (model space).
        const modelLen = pathEl.getTotalLength();
        return finalizeLength(modelLen, view);
      }

      if (typeof view.getConnectionPathData === 'function' && joint.g && typeof joint.g.Path === 'function') {
        const d = view.getConnectionPathData();
        if (d) {
          const p = new joint.g.Path(d);
          const modelLen = p.length();
          return finalizeLength(modelLen, view);
        }
      }
    }
  }

  // 3) Final fallback: Euclidean polyline length through vertices & endpoints
  const vertices = this.vertices() || [];
  const source = this.source();
  const target = this.target();

  if (!source || !target) return 0;

  const toPoint = (end) => {
    if (end.id) {
      const c = this.graph.getCell(end.id);
      return c?.getBBox().center() ?? end;
    }
    return end;
  };

  const points = [toPoint(source), ...vertices, toPoint(target)];

  let modelLen = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    modelLen += Math.hypot(dx, dy); // Euclidean, not Manhattan
  }

  // Keep your existing tools behavior (optional)
  if (paper) {
    const cellView = paper.findViewByModel(cell);
    if (cellView) {
      cellView.addTools(
        new joint.dia.ToolsView({
          name: 'length-update',
          tools: [new joint.linkTools.Vertices()],
        })
      );
      maybeAttachPortsUpdater(cellView);
    }
  }

  return finalizeLength(modelLen);
};

const cache = new Map();
export let app;
(function (app) {
  class Link extends joint.shapes.standard.Link {
    constructor() {
      super(...arguments);
      this.defaultLabel = {
        attrs: {
          rect: {
            fill: '#ffffff',
            stroke: '#353535',
            strokeWidth: 1,
          },
          text: {
            fontWeight: 'Normal',
            fontSize: 11,
            fill: '#353535',
          },
        },
        size: this.calculateLabelSize('Default Label'),
      };
      this.getDataWidthCached = function (d) {
        if (cache.has(d)) {
          return cache.get(d);
        } else {
          const bbox = new joint.g.Path(d).bbox();
          cache.set(d, bbox ? bbox.width : 0);
          return cache.get(d);
        }
      };
    }

    static connectionPoint(line, view, magnet, _opt, type, linkView) {
      // FIXME: marker offset may be broken? (check source vs. destination)
      // const link = linkView.model;
      // const markerWidth = link.get('type') === 'app.Link' ? link.getMarkerWidth(type) : 0;
      // const opt = { offset: markerWidth, stroke: true };
      const opt = { stroke: true };

      const modelType = view.model.get('type');
      if (modelType === 'standard.InscribedImage') {
        opt.selector = 'border';
      }

      return joint.connectionPoints.boundary.call(this, line, view, magnet, opt, type, linkView);
    }

    initialize() {
      super.initialize(...arguments);

      this.on('change:target', () => {
        this.attr({
          'line/targetMarker': {
            type: 'path',
            d: 'M 0 -5 10 0 0 5 z',
            fill: '#000000',
            stroke: 'none',
            refX: 0,
          },
          'line/sourceMarker': {
            type: 'path',
            d: 'M 0 0 0 0',
          },
        });
      });

      this.on('change:linkType', function (link, linkType) {
        // FIXME: not used? to be removed if confirmed
        const linkAttrs = getAttrsByLinkType(linkType);
        Object.entries(linkAttrs).forEach(([attrName, attrValue]) => link.attr(attrName, attrValue));
      });
    }

    defaults() {
      const defaultType = 'bagage_bac';
      const defaultAttrs = getAttrsByLinkType(defaultType);
      return joint.util.defaultsDeep(
        {
          type: 'standard.Link',
          router: {
            name: 'normal',
          },
          connector: {
            name: 'straight',
          },
          labels: [],
          linkType: defaultType,
          attrs: {
            line: {
              stroke: '#888888',
              strokeWidth: 2,
              strokeDasharray: '0',
              fill: 'none',
              sourceMarker: {
                type: 'path',
                d: 'M 0 0 0 0',
                stroke: 'none',
              },
              targetMarker: {
                type: 'path',
                d: 'M 10 -5 0 0 10 5 z',
                stroke: 'none',
                refX: 0,
              },
              ...defaultAttrs.line,
            },
          },
        },
        joint.shapes.standard.Link.prototype.defaults
      );
    }

    getMarkerWidth(type) {
      const d = type === 'source' ? this.attr('line/sourceMarker/d') : this.attr('line/targetMarker/d');
      return this.getDataWidth(d);
    }

    getDataWidth(d) {
      return this.getDataWidthCached(d);
    }

    calculateLabelSize(text) {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      context.font = '11px Roboto';
      const metrics = context.measureText(text);
      return {
        width: metrics.width + 20,
        height: 11 + 10,
      };
    }
  }
  app.Link = Link;
})(app || (app = {}));

export const standard = joint.shapes.standard;

export const appShapes = {
  ...shapes.standard,
  Div,
  Tri,
  Stock,
};
